/**
 * MobileReportsList
 *
 * Mobile card-based replacement for the PrimeReact DataTable in ReportsView.
 * Used for both view 0 (Reports) and view 1 (Exports SILAE).
 * Supports search, pagination, export download per row.
 *
 * All components from MUI.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  Divider,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 10;

// Map PrimeReact Tag severity names to MUI Chip colors
const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

/**
 * @param {Object} props
 * @param {Array}  props.reports - filtered reports list (already filtered by isSilae)
 * @param {Function} props.onExport - called with report data to download
 */
export default function MobileReportsList({ reports, onExport }) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  // Filter data by search text
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return reports || [];
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return (reports || []).filter((report) => {
      const searchStr = [
        report.company,
        report.team,
        report.status,
        report.createdBy,
        report.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((term) => searchStr.includes(term));
    });
  }, [reports, searchText]);

  // Reset page when filter changes
  React.useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusColor = (status) => {
    const severity = getTagClass(status);
    return severityToColor[severity] || "default";
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: 0,
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": { fontSize: "16px" },
          "& .MuiOutlinedInput-root": {
            borderRadius: "14px",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(12px)",
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,0,0,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover fieldset": {
              borderColor: "rgba(0,0,0,0.15)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "warning.main",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.15)",
            },
          },
        }}
      />

      {/* Results count */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          letterSpacing: "0.02em",
          opacity: 0.7,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.12)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedData.map((report, index) => (
          <Fade in key={report.id} timeout={200 + index * 40}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow:
                  "0 2px 8px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.06)",
                transition:
                  "transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "scale(0.98)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                },
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Top row: company + status */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      lineHeight: 1.3,
                      letterSpacing: "-0.01em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      mr: 1,
                    }}
                  >
                    {report.company || "-"}
                  </Typography>
                  {report.status && (
                    <Chip
                      label={<Langs str={report.status} />}
                      size="small"
                      color={getStatusColor(report.status)}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: 24,
                        borderRadius: "8px",
                      }}
                    />
                  )}
                </Box>

                {/* Client / Team */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.78rem", display: "block", mb: 0.5 }}
                >
                  <Langs str="Client" />: {report.team || "-"}
                </Typography>

                {/* Description preview */}
                {report.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.75rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      opacity: 0.7,
                      mb: 0.5,
                    }}
                  >
                    {report.description}
                  </Typography>
                )}

                {/* Created info row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.72rem", opacity: 0.6 }}
                  >
                    {report.createdBy || "-"} - {report.createdAt || "-"}
                  </Typography>
                </Box>
              </CardContent>

              {/* Action buttons row */}
              <Divider sx={{ opacity: 0.4, borderColor: "rgba(0,0,0,0.06)" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 1,
                  py: 0.25,
                }}
              >
                <Tooltip title="Export">
                  <span>
                    <IconButton
                      size="small"
                      disabled={report.status === "VALIDATED"}
                      onClick={() => onExport(report)}
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                        borderRadius: "12px",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": {
                          backgroundColor: "rgba(255,144,21,0.08)",
                          transform: "scale(1.08)",
                          color: "rgb(255,144,21)",
                        },
                        "&:active": { transform: "scale(0.95)" },
                      }}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Card>
          </Fade>
        ))}

        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
